// frontend/src/core/proxyParser.ts
// Утиліта для автоматичного парсингу рядків проксі різних форматів

export interface ParsedProxy {
  host: string;
  port: string;
  username?: string;
  password?: string;
  protocol?: 'http' | 'socks5';
}

/**
 * Парсить рядок проксі в об'єкт з полями host, port, username, password, protocol.
 * Підтримувані формати:
 * - http://51.81.195.183:56788:USS798200:34xprkip@ (і без trailing @)
 * - socks5://51.81.195.183:56788:USS798200:34xprkip@
 * - 51.81.195.183:56788:USS798200:34xprkip@
 * - http://user:pass@host:port (стандартний URL)
 * - socks5://user:pass@host:port
 * - user:pass@host:port
 * - host:port
 * - http://host:port
 */
export function parseProxyString(raw: string): ParsedProxy | null {
  if (!raw || typeof raw !== 'string') return null;
  let text = raw.trim();
  if (!text) return null;

  let protocol: 'http' | 'socks5' | undefined = undefined;

  // Виділяємо протокол, якщо присутній
  if (/^socks[45]:\/\//i.test(text)) {
    protocol = 'socks5';
    text = text.replace(/^socks[45]:\/\//i, '');
  } else if (/^https?:\/\//i.test(text)) {
    protocol = 'http';
    text = text.replace(/^https?:\/\//i, '');
  }

  // Видаляємо завершальні слеші, пробіли та символи '@' на кінці
  text = text.replace(/[/@\s]+$/, '').trim();
  if (!text) return null;

  // Формат 1: host:port:username:password (наприклад: 51.81.195.183:56788:USS798200:34xprkip)
  const parts = text.split(':');
  if (parts.length >= 4 && /^\d+$/.test(parts[1].trim())) {
    const host = parts[0].trim();
    const port = parts[1].trim();
    const username = parts[2].trim();
    const password = parts.slice(3).join(':').trim();

    if (host && port) {
      return {
        host,
        port,
        username,
        password,
        ...(protocol ? { protocol } : {}),
      };
    }
  }

  // Формат 2: username:password@host:port
  if (text.includes('@')) {
    const atIdx = text.lastIndexOf('@');
    const authPart = text.substring(0, atIdx).trim();
    const hostPortPart = text.substring(atIdx + 1).trim();

    const colonIdx = authPart.indexOf(':');
    const username = colonIdx !== -1 ? authPart.substring(0, colonIdx).trim() : authPart;
    const password = colonIdx !== -1 ? authPart.substring(colonIdx + 1).trim() : '';

    const hostPortSegments = hostPortPart.split(':');
    const host = hostPortSegments[0]?.trim();
    const port = hostPortSegments[1]?.trim();

    if (host && port && /^\d+$/.test(port)) {
      return {
        host,
        port,
        username,
        password,
        ...(protocol ? { protocol } : {}),
      };
    }
  }

  // Формат 3: host:port
  if (parts.length === 2) {
    const host = parts[0].trim();
    const port = parts[1].trim();
    if (host && /^\d+$/.test(port)) {
      return {
        host,
        port,
        ...(protocol ? { protocol } : {}),
      };
    }
  }

  // Формат 4: протокол + host (без порту)
  if (protocol && text && !text.includes(':') && !text.includes('@')) {
    return {
      host: text,
      port: '',
      protocol,
    };
  }

  return null;
}
